import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import './ImageUploader.css';

// Human-readable byte size, e.g. 1536 -> "1.5 KB".
const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // keep in step with MAX_IMAGE_UPLOAD_BYTES in server.js

// The parent (FieldImageGallery) gets `uploadFiles` through the ref so files
// dropped anywhere on the gallery go through the same checks and queue.
const ImageUploader = forwardRef(({ locationId, fieldType, onImageUploaded, questId, disabled, imageCount = 0, maxImages = 10 }, ref) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total } while a batch runs
  const [uploadErrors, setUploadErrors] = useState([]); // one line per file that did not make it
  const [uploadInfo, setUploadInfo] = useState(null);
  const fileInputRef = useRef(null);

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // One file to /api/upload-image. Throws with the server's message on failure.
  const uploadOne = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('locationId', locationId);
    formData.append('fieldType', fieldType);
    formData.append('questId', questId);
    // Even though server blocks writes to prod, we send 'quests_test' to be explicit.
    formData.append('table', 'quests_test');

    const response = await fetch('/api/upload-image', {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text).error || text; } catch { /* not JSON: keep the raw body */ }
      throw new Error(`HTTP ${response.status}: ${message}`);
    }
    return response.json();
  };

  // Several files go up one after another, in the order they were picked, so
  // the gallery keeps that order and the server's 10-image cap is never raced.
  const uploadFiles = async (picked) => {
    if (!picked.length || isUploading || disabled) return;

    const errors = [];
    const valid = picked.filter((file) => {
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: not an image`);
        return false;
      }
      // The cap is high because the server downscales and re-encodes the upload.
      if (file.size > MAX_UPLOAD_BYTES) {
        errors.push(`${file.name}: larger than 25 MB`);
        return false;
      }
      return true;
    });
    const room = Math.max(0, maxImages - imageCount);
    const batch = valid.slice(0, room);
    valid.slice(room).forEach((file) => {
      errors.push(`${file.name}: skipped, the field holds at most ${maxImages} images`);
    });

    setUploadErrors(errors);
    setUploadInfo(null);
    if (!batch.length) return;

    setIsUploading(true);
    let originalTotal = 0;
    let finalTotal = 0;
    let uploaded = 0;
    for (let i = 0; i < batch.length; i += 1) {
      setProgress({ done: i, total: batch.length });
      try {
        const data = await uploadOne(batch[i]);
        uploaded += 1;
        if (Number.isFinite(data.originalSize) && Number.isFinite(data.finalSize)) {
          originalTotal += data.originalSize;
          finalTotal += data.finalSize;
        }
        // Uploads append to the field's gallery; let the parent refetch it.
        if (onImageUploaded) onImageUploaded(data);
      } catch (error) {
        console.error(`Image upload error (${batch[i].name}):`, error);
        errors.push(`${batch[i].name}: ${error.message}`);
        setUploadErrors([...errors]);
      }
    }
    setProgress(null);
    setIsUploading(false);

    // Surface how much the server's pass shrank (or grew) what went up.
    if (uploaded && originalTotal > 0) {
      const pct = Math.round((1 - finalTotal / originalTotal) * 100);
      let note;
      if (pct > 0) note = `saved ${pct}%`;
      else if (pct < 0) note = `grew ${-pct}%`;
      else note = 'no change';
      const count = batch.length > 1 ? `${uploaded} of ${batch.length} uploaded, ` : '';
      setUploadInfo(`${count}${formatBytes(originalTotal)} → ${formatBytes(finalTotal)} (${note})`);
    }
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    uploadFiles(picked);
  };

  useImperativeHandle(ref, () => ({ uploadFiles }));

  return (
    <div className="image-uploader">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        multiple
        className="file-input"
      />
      
      <button
        className="upload-button"
        onClick={handleUploadClick}
        disabled={isUploading || disabled || imageCount >= maxImages}
        title={imageCount >= maxImages ? `A field can hold at most ${maxImages} images` : 'Pick files or drop them onto the gallery'}
      >
        {isUploading
          ? `Uploading${progress && progress.total > 1 ? ` ${progress.done + 1}/${progress.total}` : ''}…`
          : imageCount > 0 ? '+ Add Images' : 'Add Images'}
      </button>
      
      {uploadErrors.map((line) => (
        <div key={line} className="upload-error">{line}</div>
      ))}

      {uploadInfo && (
        <div className="upload-info">{uploadInfo}</div>
      )}
    </div>
  );
});

export default ImageUploader; 